import {Positionable, ReelView, SimpleSymbolState} from "../../core/reel/simplereel/SimpleReelModel";
import {TheCageStatics} from "../config/TheCageStatics";
import {gsap} from "gsap";
import {TheCageGameAssets} from "../TheCageGameAssets";
import {TheCage} from "../TheCage";
import {SpriteUtils} from "../utils/SpriteUtils";
import {SpineGSAPWrapper} from "../animation/SpineGSAPWrapper";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";

export enum TheCageSymbolStates {
    NORMAL,
    SPIN,
    WIN_TINY,
    WIN_SMALL,
    WIN_MEDIUM,
    WIN_LARGE,
    NO_WIN,
    EXPLODE,
    WIN_POP,
    REMOVE,
    LANDING,
    ATTENTION,
    WIN_HIGHLIGHT_LARGE,
    WIN_EXPLODE_LARGE,
    WIN_REVERSE_LARGE,
}

export enum SYMBOL_NAME {
    WILD = "W",
    SCATTER = "S",
    M1 = "M1",
    M2 = "M2",
    M3 = "M3",
    M4 = "M4",
    M5 = "M5",
    L1 = "L1",
    L2 = "L2",
    L3 = "L3",
    L4 = "41",
    L5 = "L5",
}


export class TheCageSymbol extends PIXI.Container implements Positionable {
    public symbolName:string;
    private _overlay:PIXI.Sprite;
    protected view:PIXI.Container;

    private mainSprite:PIXI.Sprite;
    private multiplier: PIXI.spine.Spine;
    private _stateAnimation:gsap.core.Timeline;
    private state:TheCageSymbolStates;
    protected _spine:PIXI.spine.Spine;
    private reelsView:ReelView;
    private textureSwapAnimation:gsap.core.Timeline;

    constructor(symbolName:string, initState:TheCageSymbolStates, reelsView:ReelView) {
        super();
        this.reelsView = reelsView;

        this.symbolName = symbolName;

        this._overlay = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._overlay.tint = 0xff0000;
        this._overlay.alpha = 0.3;
        this._overlay.width = TheCageStatics.SYMBOL_SPRITE_WIDTH;
        this._overlay.height = TheCageStatics.SYMBOL_SPRITE_HEIGHT;


        this.mainSprite = new PIXI.Sprite(TheCage.assetLoader.getTextures(TheCageGameAssets.getSymbolStateTexturePath(this.symbolName))[0]);
        this.mainSprite.anchor.set(0.5,0.5);

        //this.multiplier = new PIXI.spine.Spine(TheCage.assetLoader.getSpineAsset(TheCageGameAssets.FS_REEL_EFFECT));
        //this.multiplier.visible = false;

        this.view = new PIXI.Container();
        this.view.addChild(this.mainSprite);

        this.addChild(this.view);

    }

    // dont initialize spine until it is needed, might be premature optimization but creating 24+ spines every spin
    // and then not doing anything with them seems wasteful

    // used for regular play
    public setMultiplier(): gsap.core.Timeline {
        if (this.multiplier == undefined){
            this.multiplier = new PIXI.spine.Spine(TheCage.assetLoader.getSpineAsset(TheCageGameAssets.FS_REEL_EFFECT));
            this.view.addChild(this.multiplier)
        }
        const tl = gsap.timeline();
        tl.add(SpineGSAPWrapper.createTimeline(this.multiplier, 1, "effect_activate_x2"));
        tl.add(() => { this.multiplier.state.setAnimation(1, "effect_x2", true)});
        return tl;
    }

    // used for restore
    public setMultiplierInstant(): gsap.core.Timeline {
        if (this.multiplier == undefined){
            this.multiplier = new PIXI.spine.Spine(TheCage.assetLoader.getSpineAsset(TheCageGameAssets.FS_REEL_EFFECT));
            this.view.addChild(this.multiplier)
        }
        const tl = gsap.timeline();
        tl.add(() => { this.multiplier.state.setAnimation(1, "effect_x2", true)});
        return tl;
    }

    public preTransformAnimation(level:number): gsap.core.Timeline {
        if (this.multiplier == undefined) {
            this.multiplier = new PIXI.spine.Spine(TheCage.assetLoader.getSpineAsset(TheCageGameAssets.FS_REEL_EFFECT));
            this.view.addChild(this.multiplier)
        }

        const tl = gsap.timeline();
        const highlightColor = ["green", "red", "yellow"][level];
        tl.add(SpineGSAPWrapper.createTimeline(this.multiplier, 0, `hl_${highlightColor}_${this.symbolName}`));
        return tl;
    }

    public transformAnimation(level:number): gsap.core.Timeline {
        let hasMultiplier = true;
        if (this.multiplier == undefined) {
            this.multiplier = new PIXI.spine.Spine(TheCage.assetLoader.getSpineAsset(TheCageGameAssets.FS_REEL_EFFECT));
            this.view.addChild(this.multiplier)
            hasMultiplier = false;
        }

        const tl = gsap.timeline();
        const highlightColor = ["green", "red", "yellow"][level];
        tl.add(SpineGSAPWrapper.createTimeline(this.multiplier, 0, `effect_activate_arrow_${highlightColor}`));

        return tl;
    }

    public removeMultiplier() {
        this.multiplier.state.setEmptyAnimation(0, 0);
        this.multiplier.visible = false;
    }

    private getSpinePoolName(symbolName:string){
        let spinePoolName = "";

        if (TheCageStatics.isLowSymbol(symbolName)){
            spinePoolName = TheCageGameAssets.L_SPINE
        } else if (symbolName == "M1"){
            spinePoolName = TheCageGameAssets.M1_SPINE;
        }
        else if (symbolName == "M2"){
            spinePoolName = TheCageGameAssets.M2_SPINE;
        }
        else if (symbolName == "M3"){
            spinePoolName = TheCageGameAssets.M3_SPINE;
        }
        else if (symbolName == "M4"){
            spinePoolName = TheCageGameAssets.M4_SPINE;
        }
        else if (symbolName == "M5"){
            spinePoolName = TheCageGameAssets.M5_SPINE;
        }
        else if (symbolName == "S1" || symbolName == "S2"){
            spinePoolName = TheCageGameAssets.S_SPINE;
        }
        else if (symbolName == "WB"){
            spinePoolName = TheCageGameAssets.WB_SPINE;
        }
        else if (symbolName == "BN"){
            spinePoolName = TheCageGameAssets.BN_SPINE;
        }

        return spinePoolName;
    }

    public getSymbolSpine(symbolName:string, setSkinBySymName:boolean = false):PIXI.spine.Spine | undefined {
        if (this._spine){
            return this._spine;
        }

        const spinePoolName = this.getSpinePoolName(symbolName);

        if (spinePoolName == ""){
            return undefined;
        }

        let spine =  TheCage.graphicsPool.getSpinePool(spinePoolName).withdraw();

        if (setSkinBySymName){
            spine.skeleton.setSkinByName(symbolName);
        }
        spine.state.setAnimation(0,"base", false);
        spine.autoUpdate = false;
        spine.update(0);

        this._spine = spine;
        return this._spine;
    }

    destroy(options?:{children?:boolean; texture?:boolean; baseTexture?:boolean}) {
        if (this._spine) {
            this._spine.state.setEmptyAnimation(0,0);
            this._spine.autoUpdate = false;
            this._spine.update(0);
            TheCage.graphicsPool.getSpinePool(this.getSpinePoolName(this.symbolName)).deposit(this._spine);
        }
        super.destroy(options);
    }

    setAvalancheState(state:SimpleSymbolState):void {
        switch (state) {
            case SimpleSymbolState.NORMAL:
                this.setState(TheCageSymbolStates.NORMAL);
                break;
            case SimpleSymbolState.DROP_IN:
                this.setState(TheCageSymbolStates.SPIN, "_spin", 0.3);
                break;
            case SimpleSymbolState.DROP_OUT:
                this.setState(TheCageSymbolStates.SPIN, "_spin", 0.3);
                this.moveToNormalLayer();
                break;
            case SimpleSymbolState.COLLAPSE_L2R:
                this.setState(TheCageSymbolStates.SPIN);
                break;
            case SimpleSymbolState.COLLAPSE_R2L:
                this.setState(TheCageSymbolStates.SPIN);
                break;
            case SimpleSymbolState.LANDING:
                this.setState(TheCageSymbolStates.NORMAL);
                //this.getStateAnimation(OctagonSymbolStates.LANDING);
                break;

        }
    }

    private setSpriteTextureHard(textureName:string = "") {
        this.mainSprite.texture = this.getTexture(textureName);
    }

    private getTexture(textureName:string) {
        if(textureName == "none") {
            return PIXI.Texture.EMPTY;
        }
        return TheCage.assetLoader.getTextures(TheCageGameAssets.getSymbolStateTexturePath(this.symbolName, textureName))[0];
    }

    protected setSpriteTexture(textureName:string = "", duration:number = 0.1):gsap.core.Timeline {
        const tl = gsap.timeline();
        if(duration == 0) {
            duration = 0.0001;
        }

        let prevAnim = this.textureSwapAnimation;
        tl.add(()=>{
            if (prevAnim && prevAnim.isActive()){
                prevAnim.progress(1);
            }
        })

        if(textureName == "none") {
            const oldAlpha = this.mainSprite.alpha;
            tl.add(gsap.to(this.mainSprite, {duration : duration, alpha : 0}))
            tl.add(() => {
                this.mainSprite.texture = PIXI.Texture.EMPTY;
                this.mainSprite.alpha = oldAlpha;
            });
        }
        else {
            const texture = this.getTexture(textureName);
            const tempSprite = SpriteUtils.makeWithTexture(texture, this.mainSprite.position, this.mainSprite.anchor, 0, this.mainSprite.tint);

            tl.add(() => {
                if(this.mainSprite && this.mainSprite.parent) {
                    this.mainSprite.parent.addChildAt(tempSprite, this.view.getChildIndex(this.mainSprite) + 1);
                }
            })
            tl.add(gsap.to(tempSprite, {duration : duration, alpha : this.mainSprite.alpha}))
            tl.add(() => {
                if(this.mainSprite && this.mainSprite.parent) {
                    this.mainSprite.texture = texture;
                    this.mainSprite.parent.removeChild(tempSprite);
                }
            });
        }

        this.textureSwapAnimation = tl;

        return tl;
    }

    protected setState(state:TheCageSymbolStates, textureName?:string, duration?:number) {
        this.state = state;
        this.setSpriteTexture(textureName, duration);
    }

    public moveToNormalLayer(object:PIXI.DisplayObject = this) {
        this.reelsView.layers.maskedNormal.addChild(object);
    }

    protected moveToWinLayer(object:PIXI.DisplayObject = this) {
        this.reelsView.layers.win.addChild(object);
    }

    protected moveToOverlayLayer(object:PIXI.DisplayObject = this) {
        this.reelsView.layers.overlay.addChild(object);
    }

    public getStateAnimation(state:TheCageSymbolStates):gsap.core.Timeline{
            const tl:gsap.core.Timeline = gsap.timeline();

/*            if (this._stateAnimation && this._stateAnimation.isActive()){
                this._stateAnimation.progress(1);
            }*/
            this._stateAnimation = tl;

            switch(state) {
                case TheCageSymbolStates.SPIN:
                   // tl.add(() => this.setState(state, "_spin"));
                    break;

                case TheCageSymbolStates.WIN_TINY:
                    tl.add(() => this.moveToWinLayer());
                    tl.add(this.getWinAnimation(state));
                    break;
                case TheCageSymbolStates.WIN_SMALL:
                    tl.add(() => this.moveToWinLayer());
                    tl.add(this.getWinAnimation(state));
                    break;
                case TheCageSymbolStates.WIN_MEDIUM:
                    tl.add(() => this.moveToWinLayer());
                    tl.add(this.getWinAnimation(state));
                    break;
                case TheCageSymbolStates.WIN_LARGE:
                    tl.add(() => this.moveToWinLayer());
                    tl.add(this.getWinAnimation(state));
                    break;

                case TheCageSymbolStates.WIN_HIGHLIGHT_LARGE:
                    tl.add(() => this.moveToWinLayer());
                    tl.add(this.getWinHighlight(state));
                    break;
                case TheCageSymbolStates.WIN_EXPLODE_LARGE:
                    tl.add(() => this.moveToWinLayer());
                    tl.add(this.getWinExplosion(state));
                    break;
                case TheCageSymbolStates.WIN_REVERSE_LARGE:
                    tl.add(() => this.moveToWinLayer());
                    tl.add(this.getWinReverse(state));
                    break;

                case TheCageSymbolStates.NO_WIN:
                    tl.add(() => this.setState(state, "_noWin", 0.3));
                    if (this.symbolName != "S1") {
                        tl.add(() => this.moveToNormalLayer());
                    }
                    //tl.add(() => this.removeWinFrame());

                    break;
                case TheCageSymbolStates.LANDING:
                    tl.add(this.getLandingAnimation(state));
                    //tl.add(() => this.setState(state, "_noWin", 0.3));
                    //tl.add(() => this.moveToNormalLayer());
                    //tl.add(() => this.removeWinFrame());
                    break;
                case TheCageSymbolStates.EXPLODE:
                    tl.add(this.getExplosionAnimation());
                    break;
                case TheCageSymbolStates.REMOVE:
                    tl.add(this.getRemoveAnimation());
                    break;
                case TheCageSymbolStates.WIN_POP:
                    tl.add(this.getWinPopAnimation());
                    break;
                case TheCageSymbolStates.ATTENTION:
                    tl.add(this.getAttentionAnimation(this.state));

                    break;
                default:
                    //tl.add(()=>this.deleteWinFrame());
                    tl.add(()=>{
                        if (this._spine){
                            this._spine.tint = 0xFFFFFF;
                            this._spine.update(0);
                        }
                    })
                    tl.add(() => this.setState(state,undefined, 0.1));
            }

            if(tl.duration() == 0) {
                tl.add(gsap.to(this, {duration : 0.0001}));
            }

            return this._stateAnimation;
        }


    public getShakeAnimation(duration:number = 1.715):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this.position, {
            duration : duration,
            x : "-=3",
            ease : TheCageStatics.bigWinAnticipationWiggle
        }));
        return tl.reverse();
    }


    protected getWinHighlightSuffix(state:TheCageSymbolStates):string {
        let  highlightSuffix = "Tiny";
        if (state == TheCageSymbolStates.WIN_SMALL){
            highlightSuffix = "Small";
        }
        else if (state == TheCageSymbolStates.WIN_MEDIUM){
            highlightSuffix = "Mid";
        }

        else if (state == TheCageSymbolStates.WIN_LARGE){
            highlightSuffix = "Large";
        }

        return highlightSuffix;
    }

    public getWinHighlight(state:TheCageSymbolStates):gsap.core.Timeline {
        const highlightSuffix = this.getWinHighlightSuffix(state);
        const tl = gsap.timeline();
        const spine = this.getSymbolSpine(this.symbolName, TheCageStatics.isLowSymbol(this.symbolName));
        if (spine){
            tl.add(()=>{
                this.view.addChild(spine);
                this.setState(TheCageSymbolStates.WIN_LARGE, "none");
                if (this.multiplier != undefined) {
                    this.view.removeChild(this.multiplier);
                    this.view.addChild(this.multiplier);
                }
            })
            tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, `WinHL_${highlightSuffix}`,1,0,false));
        }
        return tl;
    }

    public getWinReverse(state:TheCageSymbolStates):gsap.core.Timeline {
        const highlightSuffix = this.getWinHighlightSuffix(state);
        const tl = gsap.timeline();
        const spine = this.getSymbolSpine(this.symbolName, TheCageStatics.isLowSymbol(this.symbolName));
        if (spine){
            tl.add(()=>{
                this.view.addChild(spine);
                this.setState(TheCageSymbolStates.WIN_LARGE, "none");
                if (this.multiplier != undefined) {
                    this.view.removeChild(this.multiplier);
                    this.view.addChild(this.multiplier);
                }
            })
            tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, `WinHL_Large_Rv`,1,0,false));
        }
        return tl;
    }

    // only for use in freespins so we dont have to worry about handling wild bombs not disappearing like in regular wins
    public getWinExplosion(state:TheCageSymbolStates):gsap.core.Timeline {
        const tl = gsap.timeline();
        const spine = this.getSymbolSpine(this.symbolName, TheCageStatics.isLowSymbol(this.symbolName));
        if (spine) {
            tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "WinEff_A",1,0, false));
            if (this.multiplier != undefined) {
                tl.add(gsap.to(this.multiplier, {alpha: 0, duration: 0.1}), '<0.18');
                tl.add(() => {
                    this.multiplier.visible = false
                }, '<');
            }
        } else {
            tl.add(gsap.to(this.mainSprite, {duration:0.1,alpha:0}));
        }
        return tl;
    }

    public getWinAnimation(state:TheCageSymbolStates):gsap.core.Timeline {

        const highlightSuffix = this.getWinHighlightSuffix(state);
        const tl = gsap.timeline();
        const spine = this.getSymbolSpine(this.symbolName, TheCageStatics.isLowSymbol(this.symbolName));
        if (spine){
            tl.add(()=>{
                this.view.addChild(spine);
                this.setState(TheCageSymbolStates.WIN_LARGE, "none");
                if (this.multiplier != undefined) {
                    this.view.removeChild(this.multiplier);
                    this.view.addChild(this.multiplier);
                }
            })
            tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, `WinHL_${highlightSuffix}`,1,0,false));

            if (this.symbolName != "WB"){
                tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "WinEff_A",1,0, false));
            }
            if (this.multiplier != undefined) {
                tl.add(gsap.to(this.multiplier, {alpha: 0, duration: 0.1}), '<0.18');
                tl.add(() => {this.multiplier.visible = false}, '<');
            }
        } else {
            tl.add(gsap.to(this.mainSprite, {duration:0.1,alpha:0}));
        }
        return tl;
    }

    protected getLandingAnimation(state:TheCageSymbolStates):gsap.core.Timeline {
        return gsap.timeline();
    }
    protected getAttentionAnimation(state:TheCageSymbolStates):gsap.core.Timeline {
        return gsap.timeline();
    }

    protected getExplosionAnimation():gsap.core.Timeline {
        const tl = gsap.timeline();
        if (this.state == TheCageSymbolStates.EXPLODE){
            return tl;
        }

        const spine = this.getSymbolSpine(this.symbolName, TheCageStatics.isLowSymbol(this.symbolName));
        tl.add(() => this.moveToWinLayer());
        if (spine){
            tl.add(()=>{
                this.view.addChild(spine);
                this.setState(TheCageSymbolStates.EXPLODE, "none");
            })
            tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "WinEff_A",1,0,false));
        } else {
            tl.add(gsap.to(this, {duration:0.13, alpha: 0}));
        }
        return tl;
    }

    protected getRemoveAnimation():gsap.core.Timeline {
        const tl = gsap.timeline();
        if (this.state == TheCageSymbolStates.REMOVE || this.state == TheCageSymbolStates.EXPLODE){
            return tl;
        }

        const spine = this.getSymbolSpine(this.symbolName, TheCageStatics.isLowSymbol(this.symbolName));
        tl.add(() => this.moveToNormalLayer());
        if (spine){
            tl.add(()=>{
                this.view.addChild(spine);
                this.setState(TheCageSymbolStates.REMOVE, "none");
            })
            tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "NonWinBreak",1,0,false));
        } else {
            tl.add(gsap.to(this, {duration:0.13, alpha: 0}));
        }
        return tl;
    }

    protected getWinPopAnimation():gsap.core.Timeline {
        const tl = gsap.timeline();

        const spine = this.getSymbolSpine(this.symbolName, TheCageStatics.isLowSymbol(this.symbolName));
        tl.add(() => this.moveToWinLayer());
        if (spine){
            tl.add(()=>{
                this.view.addChild(spine);
                this.setState(TheCageSymbolStates.WIN_POP, "none");
            })
            tl.add(SpineGSAPWrapper.createTimelineForAnimation(spine, 0, "WinPop",1,0,false));
        } else {
            tl.add(gsap.to(this, {duration:0.13, alpha: 0}));
        }
        return tl;
    }
}